import { useMutation } from '@do/seashell/graphql';
import { useLocation, useNavigate } from '@do/seashell/navigation';
import { Background, Box, style } from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React, { ReactElement } from 'react';
import * as Yup from 'yup';
import background from '../assets/backgrounds/background.png';
import copy from '../copy';
import { forgotPassword as ForgotPassword, forgotPasswordVariables } from '../generated-types/forgotPassword';
import useDocumentTitle from '../hooks/use-document-title';
import { FORGOT_PASSWORD } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { Wrap } from './Common';
import ErrorBanner from './ErrorBanner';
import FormWrap from './FormWrap';
import Header from './Header';
import SpinnerButton from './SpinnerButton';
import Text from './Text';
import TextInput from './TextInput';
import Title from './Title';

const passwordForgotValidation = Yup.object().shape({
  email: Yup.string().email(copy.form.validation.email_valid).required(copy.form.validation.email_required),
});

const PasswordForgot: React.FC = (): ReactElement => {
  const location = useLocation();

  const [genericServerError, setGenericServerError] = React.useState(null);
  const [forgotPassword, { loading: isLoading }] = useMutation<ForgotPassword, forgotPasswordVariables>(
    FORGOT_PASSWORD,
  );
  const navigate = useNavigate();

  useDocumentTitle(copy.form.password_forgot_title);

  return (
    <>
      <Header isLogin={true} />
      <Background url={background} />
      <Wrap>
        <FormWrap>
          <Box display="block" px={style.vars.whitespace.medium}>
            <Title title={copy.form.password_forgot.title} />
            <Text>{copy.form.password_forgot.details}</Text>
            <Formik
              initialValues={{
                email: _get(location, 'state.email') || '',
              }}
              validationSchema={passwordForgotValidation}
              onSubmit={(values, { setErrors }) => {
                setGenericServerError(null); /* Reset displayed error */
                forgotPassword({
                  variables: {
                    forgotPasswordRequest: values,
                  },
                })
                  .then(() => {
                    navigate('/login', { state: { success: 'forgot' } });
                  })
                  .catch(handleFormError(setErrors, setGenericServerError));
              }}
            >
              {({ errors, values, touched }) => (
                <Form>
                  {genericServerError && <ErrorBanner error={genericServerError} />}

                  <TextInput
                    type="email"
                    placeholder={copy.form.password_forgot.placeholder}
                    name="email"
                    required
                    label={copy.form.password_forgot.label}
                    touched={touched.email}
                    error={errors.email}
                    value={values.email}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                  <SpinnerButton
                    type="submit"
                    fullWidth
                    spinning={isLoading}
                    disabled={isLoading}
                    loadingMessage={copy.form.password_forgot.loading}
                  >
                    {copy.form.password_forgot.button_text}
                  </SpinnerButton>
                </Form>
              )}
            </Formik>
          </Box>
        </FormWrap>
      </Wrap>
    </>
  );
};

export default PasswordForgot;
